import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { CompanyRegistrationStatus } from './enums/registration-status.js';
import { MatchingWeekSettings } from './settings.js';

@Entity()
export class CompanyRegistration extends MongoEntity {
  static override _class = 'CompanyRegistration';
  static override _collectionName = 'company-registrations';

  constructor(doc: PartialEntityData<CompanyRegistration>) {
    super(doc);
  }

  @validate(nonOptional())
  @Field(() => String)
  companyName!: string;

  @validate(nonOptional())
  @Field(() => String)
  contactFirstName!: string;

  @validate(nonOptional())
  @Field(() => String)
  contactLastName!: string;

  @validate(nonOptional())
  @Field(() => String)
  contactEmail!: string;

  // The fields below are required by the client form (see
  // company-registration-form.ts:handleSubmit), but kept as Maybe<string>
  // server-side so existing CompanyRegistration documents created when these
  // were optional can still be approved/rejected (those flows call .save()
  // on the loaded entity).
  @Field(() => String)
  address: Maybe<string>;

  @Field(() => String)
  sector: Maybe<string>;

  @Field(() => String)
  website: Maybe<string>;

  @Field(() => String)
  services: Maybe<string>;

  @Field(() => String)
  openPositions: Maybe<string>;

  @Field(() => String)
  participationReason: Maybe<string>;

  @Field(() => String)
  contactPosition: Maybe<string>;

  @Field(() => CompanyRegistrationStatus)
  status: Maybe<CompanyRegistrationStatus>;

  @Field(() => Date)
  submittedAt: Maybe<Date>;

  @Field(() => Date)
  reviewedAt: Maybe<Date>;

  @Field(() => ID)
  reviewedByUserID: Maybe<string>;

  @Field(() => ID)
  resultingCompanyID: Maybe<string>;

  @Field(() => String)
  rejectionReason: Maybe<string>;

  static override async beforeCreate(entity: CompanyRegistration): Promise<void> {
    const settingsCollection = mongo().collection(MatchingWeekSettings._collectionName);
    const settings = await settingsCollection.findOne({}, { projection: { companyRegistrationClosed: 1 } });
    if (settings?.companyRegistrationClosed === true) throw new Error('Company registration is closed');

    entity.submittedAt = new Date();
    entity.status = CompanyRegistrationStatus.PENDING;
    await super.beforeCreate(entity);
  }
}
